<script setup lang="ts">
/**
 * LeakGuard · 全站防拍照水印层
 * ------------------------------------------------------------
 * 每个页面叠加（AppShell 挂载一次，fixed 全屏、pointer-events:none 不影响交互）。
 * 三层：
 *  1. 显性层——姓名 + 手机号 + 工号平铺斜向水印，低对比度、间距适中，防随手拍照外传；
 *  2. 隐性层 A（截图取证）——每格水印下叠加 7px / 1.8% 对比度的微文本
 *     （用户 ID | 时间戳 | 会话指纹），正常使用肉眼不可感知，
 *     截图经对比度增强后可被程序 / AI 提取，用于追溯泄露截图来源；
 *  3. 隐性层 B（DOM 取证）——零宽字符把同一组身份信息编码进文本节点，
 *     无字形、不占宽，人眼与截图均不可见，HTML / 富文本拷贝层取证可解码。
 * 显性层用 SVG data-URI 平铺并在运行时生成（时间戳/指纹动态注入），
 * 亮暗主题各一份；M2 由服务端按会话下发 sig，形成「一人一水印」。
 */
import { computed } from 'vue'
import { portalAccount } from './portal/portal'

/* 水印身份跟随当前登录账号（切账号后全站水印同步换人）；手机号/工号为演示口径 */
const PHONE = '138****6688' // 演示口径（M2 取账号安全中心绑定的掩码手机号）
const EMP = 'EMP-0001'
const TS = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '')
const SIG = Math.random().toString(16).slice(2, 6)
const HIDDEN = computed(() => `${portalAccount.value.id}|${TS}|${SIG}`)
const VISIBLE = computed(() => `${portalAccount.value.name} ${PHONE} ${EMP}`)

/* 微文本与零宽编码（人眼不可见，程序可解码） */
const zw = (s: string) =>
  Array.from(s).map(c => (c.codePointAt(0) || 0).toString(2).padStart(8, '0')).join('')
    .replace(/0/g, '\u200B').replace(/1/g, '\u200C')
const zwText = computed(() => zw(`EMA:${HIDDEN.value}`))

/* 运行时生成平铺 SVG（可见行 + 隐形微文本行），亮暗两份 */
function tile(dark: boolean, visible: string, hidden: string): string {
  const fg = dark ? '%23FFFFFF' : '%230B1220'
  const visOp = dark ? '0.058' : '0.05'
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='330' height='180'%3E` +
    `%3Ctext x='26' y='84' font-size='13' letter-spacing='1.2' fill='${fg}' fill-opacity='${visOp}' transform='rotate(-21 26 84)' font-family='system-ui,-apple-system,sans-serif'%3E${encodeURIComponent(visible)}%3C/text%3E` +
    `%3Ctext x='26' y='108' font-size='7' letter-spacing='0.8' fill='${fg}' fill-opacity='0.018' transform='rotate(-21 26 108)' font-family='monospace'%3E${encodeURIComponent(hidden)}%3C/text%3E` +
    `%3C/svg%3E")`
}
const lightStyle = computed(() => ({ backgroundImage: tile(false, VISIBLE.value, HIDDEN.value) }))
const darkStyle = computed(() => ({ backgroundImage: tile(true, VISIBLE.value, HIDDEN.value) }))
</script>

<template>
  <div class="lg-root" aria-hidden="true">
    <!-- 显性平铺水印 + 隐性微文本（亮/暗各一份，CSS 切换） -->
    <div class="lg-tile lg-light" :style="lightStyle" />
    <div class="lg-tile lg-dark" :style="darkStyle" />
    <!-- 隐性层 B：零宽字符身份编码（无字形，DOM/拷贝层取证） -->
    <span class="lg-zw">{{ zwText }}</span>
  </div>
</template>

<!-- 全局样式（亮暗切换选择器需要非 scoped） -->
<style>
.lg-root { position: fixed; inset: 0; z-index: 9999; pointer-events: none; overflow: hidden; }
.lg-tile { position: absolute; inset: 0; background-repeat: repeat; user-select: none; }
.lg-light { display: block; }
.lg-dark { display: none; }
html[data-mode='dark'] .lg-light { display: none; }
html[data-mode='dark'] .lg-dark { display: block; }
.lg-zw { position: absolute; left: 0; top: 0; font-size: 0; line-height: 0; color: transparent; user-select: none; }
@media print { .lg-root { display: block; } }
</style>
